import React, {
  Dispatch,
  FC,
  SetStateAction,
  useEffect,
  useState,
} from "react";
import apiRequest from "utils/ApiRequest/apiRequest.js";
import queryString from "query-string";
import { useContent } from "contexts/contentContext/contentContext.js";
import Button from "components/Elements/Button/Button.js";
import Icon from "components/Elements/Icon/Icon";
import bytesToDisplayHelper from "utils/bytesToDisplayHelper";

export interface IFile {
  name: string;
  size: number;
}

interface IFilesDropdown {
  className: string;
  filesList: IFile[];
  iconName: string;
  title: string;
}

interface ISubmitData {
  userEmail: string;
  userMessage: string;
}

interface IReuploadErrorFilesComponent {
  submitProps: {
    title: string;
    hidden: boolean;
  };
  handleSubmit: (data: ISubmitData) => void;
  setUploadedFiles: Dispatch<SetStateAction<never[]>>;
  uploadedFiles: IFile[];
}

const getFileSize = (size: number) => {
  const sizeObj = bytesToDisplayHelper(size);
  return `${sizeObj.size} ${sizeObj.type}`;
};

const FilesDropdown: FC<IFilesDropdown> = ({
  iconName,
  className,
  filesList,
  title,
}) => {
  const [showDropdown, setShowDropdown] = useState(false);

  return (
    <div
      className={"existing-files-dropdown " + className}
      onClick={() => {
        setShowDropdown((prev) => !prev);
      }}
    >
      <div className="dropdown-title">
        <Icon name={iconName} iconClass="title-icon" />
        {title}
        <div className="flex-spacer" />
        <Icon
          name="arrow-down"
          iconClass={"dropdown-icon" + (showDropdown ? " rotated" : "")}
          gray
        />
      </div>
      <ul className={`dropdown-list${!showDropdown ? " collapsed-list" : ""}`}>
        {filesList.map((file) => (
          <li className="dropdown-list-item">
            <div className="dropdown-list-item-name">{file.name}</div>
            <div className="flex-spacer" />
            <div className="dropdown-list-item-size">
              {getFileSize(file.size)}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

const ReuploadErrorFilesComponent: FC<IReuploadErrorFilesComponent> = ({
  submitProps,
  handleSubmit,
  setUploadedFiles,
  uploadedFiles,
}) => {
  const { getTextByKey } = useContent();

  const [errorFiles, setErrorFiles] = useState([]);
  const [ownerEmail, setOwnerEmail] = useState("");

  const handleRequest = async () => {
    const { mid } = queryString.parse(window.location.search);
    const response = await apiRequest("Mails", "getErrorMailFIles", {
      mailId: mid,
    });
    if (response.status === "success") {
      setErrorFiles(response.data.errorFiles);
      setUploadedFiles(response.data.uploadedFiles);
      setUploadedFiles(response.data.uploadedFiles || []);
      setOwnerEmail(response.data.userEmail);
    }
  };

  useEffect(() => {
    handleRequest();
  }, []);

  return (
    <div className="reupload-files-component">
      <div className="reupload-error-files-title">
        {getTextByKey("reupload-files-title")}
      </div>
      <FilesDropdown
        className="error"
        filesList={errorFiles}
        iconName="warning"
        title={getTextByKey("reupload-files-error-title")}
      />
      <FilesDropdown
        className="success"
        filesList={uploadedFiles}
        iconName="check"
        title={getTextByKey("reupload-files-success-title")}
      />
      <div className="flex-spacer" />
      {submitProps.hidden ? null : (
        <Button
          onClick={() =>
            handleSubmit({
              userEmail: ownerEmail,
              userMessage: "",
            })
          }
        >
          {submitProps.title}
        </Button>
      )}
    </div>
  );
};

export default ReuploadErrorFilesComponent;
